import { STORE_EVENTS, toPromise, withStore } from '../storage/idb.js';
import type { EventQueueStore, QueuedRecord } from './queue.js';
import type { QueueRecord } from './types.js';

interface EventRow extends Record<string, unknown> {
  readonly seq?: number;
  readonly record: QueueRecord;
}

/**
 * The durable side of the queue: written before upload, deleted only on ack
 * (technical-design §11.6). `seq` is the store's autoIncrement key, so ordering
 * survives a power cut.
 */
export function createIdbEventQueueStore(db: IDBDatabase): EventQueueStore {
  return {
    append: async (record) =>
      withStore(db, STORE_EVENTS, 'readwrite', (store) =>
        toPromise<IDBValidKey>(store.add({ record } satisfies EventRow)).then((key) => Number(key)),
      ),

    head: async (limit) => {
      if (limit <= 0) return [];
      const rows = await withStore(db, STORE_EVENTS, 'readonly', (store) =>
        toPromise<EventRow[]>(store.getAll(null, limit)),
      );
      return rows
        .filter((row): row is EventRow & { seq: number } => typeof row.seq === 'number')
        .map<QueuedRecord>((row) => ({ seq: row.seq, record: row.record }));
    },

    delete: async (seqs) => {
      await withStore(db, STORE_EVENTS, 'readwrite', (store) => {
        for (const seq of seqs) store.delete(seq);
      });
    },

    count: async () =>
      withStore(db, STORE_EVENTS, 'readonly', (store) => toPromise<number>(store.count())),
  };
}
